import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, Fire, Confetti, Smiley, MagicWand, X, DownloadSimple, ArrowLeft } from '@phosphor-icons/react';
import { apiOrigin } from '@/utils/apiBase';

const API = apiOrigin();
const REACTIONS = [
  { key: 'heart', label: '❤️', Icon: Heart },
  { key: 'fire', label: '🔥', Icon: Fire },
  { key: 'party', label: '🎉', Icon: Confetti },
  { key: 'laugh', label: '😂', Icon: Smiley },
  { key: 'wow', label: '✨', Icon: MagicWand },
];

export default function GalleryPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState(null); // photo object
  const [pendingReactions, setPendingReactions] = useState({}); // photo_id -> bool

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/events/public/${slug}/gallery`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Gallery not available yet.');
      }
      setLoading(false);
    })();
  }, [slug]);

  const handleReact = useCallback(async (photo, emoji) => {
    const key = `${photo.photo_id}_${emoji}`;
    if (pendingReactions[key]) return;
    setPendingReactions(r => ({ ...r, [key]: true }));
    // Optimistic
    setData(d => ({
      ...d,
      photos: d.photos.map(p => p.photo_id === photo.photo_id
        ? { ...p, reactions: { ...(p.reactions || {}), [emoji]: ((p.reactions || {})[emoji] || 0) + 1 } }
        : p),
    }));
    try {
      const res = await axios.post(
        `${API}/api/events/public/${slug}/gallery/${photo.photo_id}/react`,
        { emoji }
      );
      // Sync server reactions
      setData(d => ({
        ...d,
        photos: d.photos.map(p => p.photo_id === photo.photo_id ? { ...p, reactions: res.data.reactions } : p),
      }));
      if (lightbox?.photo_id === photo.photo_id) {
        setLightbox(lb => ({ ...lb, reactions: res.data.reactions }));
      }
    } catch (e) {
      // Revert optimistic on error
    }
    setTimeout(() => setPendingReactions(r => { const c = { ...r }; delete c[key]; return c; }), 800);
  }, [slug, lightbox, pendingReactions]);

  const downloadPhoto = async (photo) => {
    try {
      const res = await fetch(`${API}${photo.url}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data.event.title.replace(/[^a-z0-9]/gi, '_')}_${photo.photo_id}.jpg`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { /* noop */ }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
      <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-[#0A0A0A] text-center">
      <div className="text-5xl mb-4">📸</div>
      <h1 className="text-xl font-bold text-white mb-2">Gallery not ready</h1>
      <p className="text-sm text-neutral-400 max-w-sm">{error}</p>
      <Link to={`/party/${slug}`} className="mt-6 text-xs font-bold text-orange-400 inline-flex items-center gap-1.5">
        <ArrowLeft size={14} weight="bold" /> Back to RSVP
      </Link>
    </div>
  );

  const ev = data.event;
  const themeColor = ev.theme_color || '#FF6B35';
  const photos = data.photos || [];

  return (
    <div className="min-h-screen text-white" data-no-theme-shim style={{ background: '#0A0A0A' }} data-testid="gallery-page">
      {/* Header */}
      <div className="sticky top-0 z-30 px-4 sm:px-6 py-3 backdrop-blur-xl border-b"
        style={{ background: 'rgba(10,10,10,0.85)', borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to={`/party/${slug}`} className="inline-flex items-center gap-2 text-xs font-semibold text-neutral-200 hover:text-white">
            <ArrowLeft size={14} weight="bold" /> Back
          </Link>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: themeColor }}>Photo Wall</div>
            <div className="text-sm font-bold truncate max-w-[60vw] text-white">{ev.title}</div>
          </div>
          <div className="px-2.5 py-1 rounded-full text-[10px] font-bold tabular-nums"
            style={{ background: `${themeColor}25`, color: themeColor }}>
            {photos.length} {photos.length === 1 ? 'photo' : 'photos'}
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="px-4 sm:px-6 pt-8 pb-6 text-center max-w-2xl mx-auto">
        <div className="text-4xl mb-3">{ev.theme_emoji || '📸'}</div>
        <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-white">Memories from {ev.title}</h1>
        <p className="text-sm text-neutral-300">Tap any photo to open, react & download.</p>
      </div>

      {/* Masonry */}
      {photos.length === 0 ? (
        <div className="text-center py-20 text-neutral-500">
          <div className="text-5xl mb-3 opacity-50">📷</div>
          <p className="text-sm">No photos yet — check back soon!</p>
        </div>
      ) : (
        <div className="px-3 sm:px-6 pb-24 max-w-6xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
            {photos.map((p) => (
              <PhotoTile key={p.photo_id} photo={p} themeColor={themeColor} onClick={() => setLightbox(p)} />
            ))}
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="gallery-lightbox" onClick={() => setLightbox(null)}>
          <div className="flex items-center justify-between p-4 text-white" onClick={e => e.stopPropagation()}>
            <button onClick={() => setLightbox(null)} className="p-2 rounded-full bg-white/10 hover:bg-white/20"
              data-testid="lightbox-close">
              <X size={18} weight="bold" />
            </button>
            <button onClick={() => downloadPhoto(lightbox)} data-testid="download-photo-btn"
              className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-bold inline-flex items-center gap-1.5">
              <DownloadSimple size={14} weight="bold" /> Download
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center px-2" onClick={e => e.stopPropagation()}>
            <img src={`${API}${lightbox.url}`} alt="" className="max-h-full max-w-full object-contain" data-testid="lightbox-img" />
          </div>
          {ev.gallery_allow_reactions && (
            <div className="p-4 flex justify-center gap-2" onClick={e => e.stopPropagation()}>
              {REACTIONS.map(r => (
                <button key={r.key} onClick={() => handleReact(lightbox, r.key)}
                  data-testid={`react-${r.key}`}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 hover:scale-110 transition-all active:scale-95">
                  <span className="text-lg">{r.label}</span>
                  <span className="text-xs font-bold tabular-nums min-w-[16px]">
                    {(lightbox.reactions || {})[r.key] || 0}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PhotoTile({ photo, themeColor, onClick }) {
  const totalReactions = Object.values(photo.reactions || {}).reduce((a, b) => a + b, 0);
  return (
    <button onClick={onClick} data-testid={`photo-tile-${photo.photo_id}`}
      className="relative group block w-full rounded-xl overflow-hidden bg-neutral-900 aspect-square transition-transform hover:scale-[1.02]">
      <img src={`${API}${photo.thumb_url}`} loading="lazy" alt=""
        className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      {totalReactions > 0 && (
        <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold backdrop-blur-md"
          style={{ background: 'rgba(0,0,0,0.6)', color: themeColor }}>
          ❤ {totalReactions}
        </div>
      )}
      {photo.face_count > 0 && (
        <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded-md text-[9px] font-bold backdrop-blur-md"
          style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }}>
          {photo.face_count} 👤
        </div>
      )}
    </button>
  );
}
